import {
  PortalArticleBlock,
  PortalArticleCardItem,
  PortalArticleImage,
  PortalArticleSection,
  PortalArticleSectionContentItem,
  PortalArticleTableColumnAlign,
  PortalArticleTableColumnStyle,
  PortalArticleTableColumnWidth,
  PortalArticleTopicItem,
  PortalArtigoModel,
  PortalFaqAnswerBlock,
  PortalFaqConfig,
  PortalPillarConfig,
  PortalPostSchemaExtras,
  PortalRichText,
  PortalRichTextPart,
} from '../../../models/PortalArtigoModel'
import { normalizePortalArtigoForSave } from './artigo-normalize'

type JsonRecord = Record<string, unknown>

export const PORTAL_ARTICLE_JSON_VERSION = 1 as const
export const PORTAL_ARTICLE_JSON_MAX_BYTES = 2 * 1024 * 1024

const MAX_JSON_DEPTH = 30
const MAX_JSON_ENTRIES = 50_000
const FORBIDDEN_JSON_KEYS = new Set(['__proto__', 'prototype', 'constructor'])

export interface PortalArticleTransferFileV1 {
  versao: typeof PORTAL_ARTICLE_JSON_VERSION
  artigo: PortalArtigoModel
}

export interface PortalArticleJsonTransferStats {
  rootBlocks: number
  topics: number
  images: number
}

export interface PortalArticleJsonImportResult {
  artigo: PortalArtigoModel
  stats: PortalArticleJsonTransferStats
}

export interface PortalArticleJsonExport {
  fileName: string
  text: string
  stats: PortalArticleJsonTransferStats
}

export class PortalArticleJsonTransferError extends Error {
  path?: string

  constructor(message: string, path?: string) {
    super(message)
    this.name = 'PortalArticleJsonTransferError'
    this.path = path
    Object.setPrototypeOf(this, PortalArticleJsonTransferError.prototype)
  }
}

export function createPortalArticleJsonExport(
  artigo: PortalArtigoModel
): PortalArticleJsonExport {
  const normalized = normalizePortalArtigoForSave(artigo)
  const sanitized = sanitizeArticle(normalized)
  const envelope: PortalArticleTransferFileV1 = {
    versao: PORTAL_ARTICLE_JSON_VERSION,
    artigo: sanitized,
  }

  return {
    fileName: createPortalArticleJsonFileName(artigo),
    text: `${JSON.stringify(envelope, null, 2)}\n`,
    stats: collectPortalArticleJsonStats(sanitized),
  }
}

export function createPortalArticleJsonTemplateExport(): PortalArticleJsonExport {
  const exported = createPortalArticleJsonExport({
    tipo: null,
    titulo: 'Título do novo artigo',
    slug: 'titulo-do-novo-artigo',
    resumo: 'Resumo curto que será exibido na apresentação do artigo.',
    status: 'RASCUNHO',
    tituloSeo: 'Título do novo artigo',
    descricaoSeo: 'Descrição do artigo para mecanismos de busca.',
    categoria: 'Categoria do artigo',
    tags: ['exemplo', 'substitua-estas-tags'],
    conteudo: [
      {
        type: 'intro',
        id: 'introducao',
        section: {
          id: 'introducao-section',
          title: '',
          content: [
            {
              type: 'paragraph',
              id: 'introducao-paragrafo-1',
              content: 'Escreva aqui a introdução do artigo.',
            },
          ],
        },
      },
      {
        type: 'summary',
        id: 'indice',
        title: 'Neste artigo',
        topics: [{ title: 'Tópico principal', href: '#topico-principal' }],
      },
      {
        type: 'topic',
        id: 'topico-principal',
        items: [
          {
            type: 'section',
            id: 'topico-principal-section-item',
            role: 'main',
            section: {
              id: 'topico-principal-section',
              title: 'Tópico principal',
              anchorId: 'topico-principal',
              content: [
                {
                  type: 'paragraph',
                  id: 'topico-principal-paragrafo-1',
                  content: [
                    'Use texto simples ou partes com ',
                    { type: 'strong', text: 'destaque' },
                    '.',
                  ],
                },
                {
                  type: 'image',
                  id: 'topico-principal-imagem-1',
                  image: {
                    src: '/substitua-pela-url-da-imagem-enviada-no-cms.webp',
                    alt: 'Descreva o conteúdo da imagem',
                    width: 1200,
                    height: 630,
                    caption: 'Substitua esta referência pelo upload ou URL final.',
                  },
                },
              ],
            },
          },
        ],
      },
    ],
    imagemCapaUrl: '/substitua-pela-url-da-capa-enviada-no-cms.webp',
    imagemCapaAlt: 'Descreva o conteúdo da imagem de capa',
    imagemCapaLargura: 1200,
    imagemCapaAltura: 630,
    faq: null,
    cta: null,
    cursosRelacionados: null,
    artigosRelacionados: null,
  })

  return { ...exported, fileName: 'modelo-artigo-cms.json' }
}

export function parsePortalArticleJson(text: string): PortalArticleJsonImportResult {
  if (!text.trim()) throw transferError('Informe um JSON para importar.')
  if (getUtf8ByteLength(text) > PORTAL_ARTICLE_JSON_MAX_BYTES) {
    throw transferError('O JSON deve possuir no máximo 2 MB.')
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    throw transferError('O JSON informado é inválido.')
  }

  assertSafeJsonTree(parsed, '$', 0, { entries: 0 })
  const envelope = requireRecord(parsed, '$')
  if (envelope.versao !== PORTAL_ARTICLE_JSON_VERSION) {
    throw transferError('Versão do JSON não suportada. Use a versão 1.', '$.versao')
  }
  if (!isRecord(envelope.artigo)) {
    throw transferError('O JSON deve possuir um objeto "artigo".', '$.artigo')
  }

  const artigo = sanitizeArticle(envelope.artigo, '$.artigo')
  return { artigo, stats: collectPortalArticleJsonStats(artigo) }
}

export function getUtf8ByteLength(value: string): number {
  return new TextEncoder().encode(value).byteLength
}

function sanitizeArticle(value: unknown, path = '$.artigo'): PortalArtigoModel {
  const article = requireRecord(value, path)
  const tipo = optionalEnum(article.tipo, `${path}.tipo`, ['PILLAR', ''] as const)

  return {
    tipo: tipo === 'PILLAR' ? 'PILLAR' : null,
    titulo: draftString(article.titulo, `${path}.titulo`),
    slug: draftString(article.slug, `${path}.slug`),
    resumo: optionalString(article.resumo, `${path}.resumo`),
    status: 'RASCUNHO',
    tituloSeo: optionalString(article.tituloSeo, `${path}.tituloSeo`),
    descricaoSeo: optionalString(article.descricaoSeo, `${path}.descricaoSeo`),
    categoria: optionalString(article.categoria, `${path}.categoria`),
    tags: stringArray(article.tags, `${path}.tags`),
    conteudo: sanitizeBlocks(article.conteudo, `${path}.conteudo`),
    configuracaoPillar: sanitizePillarConfig(
      article.configuracaoPillar,
      `${path}.configuracaoPillar`
    ),
    faq: sanitizeNullable(article.faq, `${path}.faq`, sanitizeFaq),
    cta: sanitizeNullable(article.cta, `${path}.cta`, sanitizeCta),
    cursosRelacionados: sanitizeNullable(
      article.cursosRelacionados,
      `${path}.cursosRelacionados`,
      sanitizeCourses
    ),
    artigosRelacionados: sanitizeNullable(
      article.artigosRelacionados,
      `${path}.artigosRelacionados`,
      sanitizeRelatedArticles
    ),
    schema: sanitizeSchema(article.schema, `${path}.schema`),
    imagemCapaUrl: optionalImageSource(article.imagemCapaUrl, `${path}.imagemCapaUrl`),
    imagemCapaAlt: optionalString(article.imagemCapaAlt, `${path}.imagemCapaAlt`),
    imagemCapaLargura: optionalNumber(
      article.imagemCapaLargura,
      `${path}.imagemCapaLargura`
    ),
    imagemCapaAltura: optionalNumber(
      article.imagemCapaAltura,
      `${path}.imagemCapaAltura`
    ),
    imagemThumbUrl: optionalImageSource(
      article.imagemThumbUrl,
      `${path}.imagemThumbUrl`
    ),
    imagemThumbAlt: optionalString(article.imagemThumbAlt, `${path}.imagemThumbAlt`),
    tituloOg: optionalString(article.tituloOg, `${path}.tituloOg`),
    descricaoOg: optionalString(article.descricaoOg, `${path}.descricaoOg`),
    imagemOgUrl: optionalImageSource(article.imagemOgUrl, `${path}.imagemOgUrl`),
    imagemOgAlt: optionalString(article.imagemOgAlt, `${path}.imagemOgAlt`),
  }
}

function sanitizeBlocks(value: unknown, path: string): PortalArticleBlock[] {
  return optionalArray(value, path).map((rawBlock, index) => {
    const blockPath = `${path}.${index}`
    const block = requireRecord(rawBlock, blockPath)
    const type = requireDiscriminator(block.type, `${blockPath}.type`, [
      'intro',
      'summary',
      'topic',
    ] as const)

    if (type === 'intro') {
      return {
        type,
        id: optionalString(block.id, `${blockPath}.id`),
        section: sanitizeSection(block.section, `${blockPath}.section`),
      }
    }
    if (type === 'summary') {
      return {
        type,
        id: optionalString(block.id, `${blockPath}.id`),
        title: optionalString(block.title, `${blockPath}.title`),
        topics: sanitizeSummaryTopics(block.topics, `${blockPath}.topics`),
      }
    }

    return {
      type,
      id: draftString(block.id, `${blockPath}.id`),
      items: sanitizeTopicItems(block.items, `${blockPath}.items`),
    }
  })
}

function sanitizeSummaryTopics(value: unknown, path: string) {
  return optionalArray(value, path).map((rawTopic, index) => {
    const topicPath = `${path}.${index}`
    const topic = requireRecord(rawTopic, topicPath)
    const subtopics = topic.subtopics == null
      ? undefined
      : optionalArray(topic.subtopics, `${topicPath}.subtopics`).map(
          (rawSubtopic, subtopicIndex) => {
            const subtopicPath = `${topicPath}.subtopics.${subtopicIndex}`
            const subtopic = requireRecord(rawSubtopic, subtopicPath)
            return {
              title: draftString(subtopic.title, `${subtopicPath}.title`),
              href: draftNavigationHref(subtopic.href, `${subtopicPath}.href`),
            }
          }
        )

    return {
      title: draftString(topic.title, `${topicPath}.title`),
      href: draftNavigationHref(topic.href, `${topicPath}.href`),
      subtopics,
    }
  })
}

function sanitizeTopicItems(value: unknown, path: string): PortalArticleTopicItem[] {
  return optionalArray(value, path).map((rawItem, index) => {
    const itemPath = `${path}.${index}`
    const item = requireRecord(rawItem, itemPath)
    const type = requireDiscriminator(item.type, `${itemPath}.type`, [
      'image',
      'section',
      'quickSummary',
    ] as const)

    if (type === 'image') return sanitizeImageItem(item, itemPath)
    if (type === 'quickSummary') {
      const quickSummary = optionalRecord(item.quickSummary, `${itemPath}.quickSummary`)
      return {
        type,
        id: optionalString(item.id, `${itemPath}.id`),
        quickSummary: {
          title: optionalString(quickSummary.title, `${itemPath}.quickSummary.title`),
          paragraph: quickSummary.paragraph == null
            ? undefined
            : sanitizeRichText(quickSummary.paragraph, `${itemPath}.quickSummary.paragraph`),
          items: sanitizeRichTextList(
            quickSummary.items,
            `${itemPath}.quickSummary.items`
          ),
        },
      }
    }

    return {
      type,
      id: optionalString(item.id, `${itemPath}.id`),
      role: requireDiscriminator(item.role, `${itemPath}.role`, [
        'main',
        'subtopic',
      ] as const),
      section: sanitizeSection(item.section, `${itemPath}.section`),
    }
  })
}

function sanitizeSection(value: unknown, path: string): PortalArticleSection {
  const section = optionalRecord(value, path)
  return {
    id: draftString(section.id, `${path}.id`),
    title: draftString(section.title, `${path}.title`),
    anchorId: optionalString(section.anchorId, `${path}.anchorId`),
    subtitle: optionalString(section.subtitle, `${path}.subtitle`),
    content: sanitizeSectionContent(section.content, `${path}.content`),
    extraAnchorIds: stringArray(section.extraAnchorIds, `${path}.extraAnchorIds`),
  }
}

function sanitizeSectionContent(
  value: unknown,
  path: string
): PortalArticleSectionContentItem[] {
  return optionalArray(value, path).map((rawItem, index) => {
    const itemPath = `${path}.${index}`
    const item = requireRecord(rawItem, itemPath)
    const type = requireDiscriminator(item.type, `${itemPath}.type`, [
      'paragraph',
      'bullets',
      'note',
      'cards',
      'image',
      'table',
      'youtube',
    ] as const)

    if (type === 'paragraph') {
      return {
        type,
        id: optionalString(item.id, `${itemPath}.id`),
        content: sanitizeRichText(item.content, `${itemPath}.content`),
      }
    }
    if (type === 'bullets') {
      return {
        type,
        id: optionalString(item.id, `${itemPath}.id`),
        items: sanitizeRichTextList(item.items, `${itemPath}.items`),
        listStyle: optionalEnum(item.listStyle, `${itemPath}.listStyle`, [
          'disc',
          'decimal',
          'none',
        ] as const),
        anchorIds: stringArray(item.anchorIds, `${itemPath}.anchorIds`),
      }
    }
    if (type === 'note') {
      return {
        type,
        id: optionalString(item.id, `${itemPath}.id`),
        content: sanitizeRichText(item.content, `${itemPath}.content`),
        anchorId: optionalString(item.anchorId, `${itemPath}.anchorId`),
        noteStyle: sanitizeNoteStyle(item.noteStyle, `${itemPath}.noteStyle`),
      }
    }
    if (type === 'cards') {
      return {
        type,
        id: optionalString(item.id, `${itemPath}.id`),
        title: optionalString(item.title, `${itemPath}.title`),
        items: sanitizeCards(item.items, `${itemPath}.items`),
      }
    }
    if (type === 'image') return sanitizeImageItem(item, itemPath)
    if (type === 'youtube') {
      return {
        type,
        id: optionalString(item.id, `${itemPath}.id`),
        videoId: draftString(item.videoId, `${itemPath}.videoId`),
      }
    }

    return sanitizeTable(item, itemPath)
  })
}

function sanitizeImageItem(value: unknown, path: string) {
  const item = requireRecord(value, path)
  return {
    type: 'image' as const,
    id: optionalString(item.id, `${path}.id`),
    anchorId: optionalString(item.anchorId, `${path}.anchorId`),
    image: sanitizeImage(item.image, `${path}.image`),
  }
}

function sanitizeImage(value: unknown, path: string): PortalArticleImage {
  const image = optionalRecord(value, path)
  return {
    src: imageSource(image.src, `${path}.src`),
    alt: draftString(image.alt, `${path}.alt`),
    width: draftNumber(image.width, `${path}.width`),
    height: draftNumber(image.height, `${path}.height`),
    href: optionalImageHref(image.href, `${path}.href`),
    caption: image.caption == null
      ? undefined
      : sanitizeRichText(image.caption, `${path}.caption`),
  }
}

function sanitizeCards(value: unknown, path: string): PortalArticleCardItem[] {
  return optionalArray(value, path).map((rawCard, index) => {
    const cardPath = `${path}.${index}`
    const card = requireRecord(rawCard, cardPath)
    const kind = requireDiscriminator(card.kind, `${cardPath}.kind`, [
      'course',
      'leadMagnet',
      'portalPost',
    ] as const)

    return kind === 'course'
      ? { kind, courseId: draftNumber(card.courseId, `${cardPath}.courseId`) }
      : { kind, slug: draftString(card.slug, `${cardPath}.slug`) }
  })
}

function sanitizeTable(value: unknown, path: string) {
  const table = requireRecord(value, path)
  const columns = optionalArray(table.columns, `${path}.columns`).map(
    (rawColumn, index) => {
      const columnPath = `${path}.columns.${index}`
      const column = requireRecord(rawColumn, columnPath)
      return {
        id: draftString(column.id, `${columnPath}.id`),
        header: draftString(column.header, `${columnPath}.header`),
        style: enumWithDefault<PortalArticleTableColumnStyle>(
          column.style,
          `${columnPath}.style`,
          ['default', 'strong', 'accent', 'badge'],
          'default'
        ),
        align: enumWithDefault<PortalArticleTableColumnAlign>(
          column.align,
          `${columnPath}.align`,
          ['left', 'center', 'right'],
          'left'
        ),
        width: enumWithDefault<PortalArticleTableColumnWidth>(
          column.width,
          `${columnPath}.width`,
          ['auto', 'narrow', 'medium', 'wide'],
          'auto'
        ),
      }
    }
  )
  const rows = optionalArray(table.rows, `${path}.rows`).map((rawRow, index) => {
    const rowPath = `${path}.rows.${index}`
    const row = requireRecord(rawRow, rowPath)
    return {
      id: draftString(row.id, `${rowPath}.id`),
      cells: sanitizeStringRecord(row.cells, `${rowPath}.cells`),
    }
  })
  const exportConfig = table.export == null
    ? undefined
    : optionalRecord(table.export, `${path}.export`)

  return {
    type: 'table' as const,
    id: draftString(table.id, `${path}.id`),
    name: draftString(table.name, `${path}.name`),
    columns,
    rows,
    export: exportConfig
      ? {
          enabled: optionalBoolean(exportConfig.enabled, `${path}.export.enabled`),
          fileName: optionalString(exportConfig.fileName, `${path}.export.fileName`),
        }
      : undefined,
  }
}

function sanitizeRichText(value: unknown, path: string): PortalRichText {
  if (value == null) return ''
  if (Array.isArray(value)) {
    return value.map((part, index) => sanitizeRichTextPart(part, `${path}.${index}`))
  }
  return sanitizeRichTextPart(value, path)
}

function sanitizeRichTextPart(value: unknown, path: string): PortalRichTextPart {
  if (typeof value === 'string') return value
  const part = requireRecord(value, path)
  const type = requireDiscriminator(part.type, `${path}.type`, ['strong', 'link'] as const)

  if (type === 'strong') {
    return {
      type,
      text: draftString(part.text, `${path}.text`),
      className: optionalString(part.className, `${path}.className`),
    }
  }

  return {
    type,
    text: draftString(part.text, `${path}.text`),
    href: draftNavigationHref(part.href, `${path}.href`),
    className: optionalString(part.className, `${path}.className`),
    target: optionalEnum(part.target, `${path}.target`, ['_blank', '_self'] as const),
    rel: optionalString(part.rel, `${path}.rel`),
  }
}

function sanitizeRichTextList(value: unknown, path: string): PortalRichText[] {
  return optionalArray(value, path).map((item, index) =>
    sanitizeRichText(item, `${path}.${index}`)
  )
}

function sanitizeNoteStyle(value: unknown, path: string) {
  if (value == null) return undefined
  const noteStyle = optionalRecord(value, path)
  const variant = optionalEnum(noteStyle.variant, `${path}.variant`, [
    'default',
    'custom',
  ] as const)
  return {
    variant: variant === 'custom' ? 'custom' as const : undefined,
    borderColor: optionalString(noteStyle.borderColor, `${path}.borderColor`),
    backgroundColor: optionalString(noteStyle.backgroundColor, `${path}.backgroundColor`),
    textColor: optionalString(noteStyle.textColor, `${path}.textColor`),
    strongColor: optionalString(noteStyle.strongColor, `${path}.strongColor`),
    linkColor: optionalString(noteStyle.linkColor, `${path}.linkColor`),
  }
}

function sanitizeFaq(value: unknown, path: string): PortalFaqConfig {
  const faq = optionalRecord(value, path)
  const whatsapp = optionalRecord(faq.whatsapp, `${path}.whatsapp`)
  return {
    anchorId: draftString(faq.anchorId, `${path}.anchorId`),
    sectionId: draftString(faq.sectionId, `${path}.sectionId`),
    title: optionalString(faq.title, `${path}.title`),
    items: optionalArray(faq.items, `${path}.items`).map((rawItem, index) => {
      const itemPath = `${path}.items.${index}`
      const item = requireRecord(rawItem, itemPath)
      return {
        id: draftString(item.id, `${itemPath}.id`),
        question: draftString(item.question, `${itemPath}.question`),
        answerText: draftString(item.answerText, `${itemPath}.answerText`),
        answer: sanitizeFaqAnswers(item.answer, `${itemPath}.answer`),
      }
    }),
    whatsapp: {
      href: draftNavigationHref(whatsapp.href, `${path}.whatsapp.href`),
      description: sanitizeRichText(
        whatsapp.description,
        `${path}.whatsapp.description`
      ),
    },
  }
}

function sanitizeFaqAnswers(value: unknown, path: string): PortalFaqAnswerBlock[] {
  return optionalArray(value, path).map((rawAnswer, index) => {
    const answerPath = `${path}.${index}`
    const answer = requireRecord(rawAnswer, answerPath)
    const type = requireDiscriminator(answer.type, `${answerPath}.type`, [
      'paragraph',
      'list',
    ] as const)
    return type === 'paragraph'
      ? {
          type,
          content: sanitizeRichText(answer.content, `${answerPath}.content`),
        }
      : {
          type,
          items: sanitizeRichTextList(answer.items, `${answerPath}.items`),
        }
  })
}

function sanitizeCta(value: unknown, path: string) {
  const cta = optionalRecord(value, path)
  return {
    title: draftString(cta.title, `${path}.title`),
    paragraphs: sanitizeRichTextList(cta.paragraphs, `${path}.paragraphs`),
    href: draftNavigationHref(cta.href, `${path}.href`),
    label: draftString(cta.label, `${path}.label`),
  }
}

function sanitizeCourses(value: unknown, path: string) {
  const courses = optionalRecord(value, path)
  return {
    anchorId: draftString(courses.anchorId, `${path}.anchorId`),
    title: draftString(courses.title, `${path}.title`),
    courseIds: numberArray(courses.courseIds, `${path}.courseIds`),
    emptyMessage: optionalString(courses.emptyMessage, `${path}.emptyMessage`),
    hideWhenEmpty: optionalBoolean(courses.hideWhenEmpty, `${path}.hideWhenEmpty`),
  }
}

function sanitizeRelatedArticles(value: unknown, path: string) {
  const related = optionalRecord(value, path)
  return {
    anchorId: draftString(related.anchorId, `${path}.anchorId`),
    title: draftString(related.title, `${path}.title`),
    articleSlugs: stringArray(related.articleSlugs, `${path}.articleSlugs`),
  }
}

function sanitizePillarConfig(value: unknown, path: string): PortalPillarConfig | undefined {
  if (value == null) return undefined
  const config = optionalRecord(value, path)
  if (config.sidebar == null) return {}

  const sidebar = optionalRecord(config.sidebar, `${path}.sidebar`)
  const indexCta = sidebar.indexCta == null
    ? undefined
    : optionalRecord(sidebar.indexCta, `${path}.sidebar.indexCta`)
  const banner = sidebar.banner == null
    ? undefined
    : optionalRecord(sidebar.banner, `${path}.sidebar.banner`)
  const bannerImage = banner?.image == null
    ? undefined
    : optionalRecord(banner.image, `${path}.sidebar.banner.image`)

  return {
    sidebar: {
      showIndex: optionalBoolean(sidebar.showIndex, `${path}.sidebar.showIndex`),
      indexTitle: optionalString(sidebar.indexTitle, `${path}.sidebar.indexTitle`),
      indexLabels: sidebar.indexLabels == null
        ? undefined
        : sanitizeStringRecord(sidebar.indexLabels, `${path}.sidebar.indexLabels`),
      indexCta: indexCta
        ? {
            label: optionalString(indexCta.label, `${path}.sidebar.indexCta.label`),
            href: optionalNavigationHref(indexCta.href, `${path}.sidebar.indexCta.href`),
          }
        : undefined,
      banner: banner
        ? {
            visible: optionalBoolean(banner.visible, `${path}.sidebar.banner.visible`),
            type: optionalEnum(banner.type, `${path}.sidebar.banner.type`, [
              'text',
              'image',
            ] as const),
            title: optionalString(banner.title, `${path}.sidebar.banner.title`),
            paragraphs: sanitizeRichTextList(
              banner.paragraphs,
              `${path}.sidebar.banner.paragraphs`
            ),
            label: optionalString(banner.label, `${path}.sidebar.banner.label`),
            href: optionalNavigationHref(banner.href, `${path}.sidebar.banner.href`),
            image: bannerImage
              ? {
                  src: optionalImageSource(
                    bannerImage.src,
                    `${path}.sidebar.banner.image.src`
                  ),
                  alt: optionalString(
                    bannerImage.alt,
                    `${path}.sidebar.banner.image.alt`
                  ),
                  href: optionalNavigationHref(
                    bannerImage.href,
                    `${path}.sidebar.banner.image.href`
                  ),
                }
              : undefined,
          }
        : undefined,
    },
  }
}

function sanitizeSchema(value: unknown, path: string): PortalPostSchemaExtras | undefined {
  if (value == null) return undefined
  const schema = optionalRecord(value, path)
  return {
    articleSection: optionalString(schema.articleSection, `${path}.articleSection`),
    about: stringArray(schema.about, `${path}.about`),
    mentions: schema.mentions == null
      ? undefined
      : optionalArray(schema.mentions, `${path}.mentions`).map((item, index) =>
          cloneSafeJson(item, `${path}.mentions.${index}`)
        ),
    wordCount: optionalNumber(schema.wordCount, `${path}.wordCount`),
    articleBody: optionalString(schema.articleBody, `${path}.articleBody`),
    courseItemList: schema.courseItemList == null
      ? undefined
      : cloneSafeJson(schema.courseItemList, `${path}.courseItemList`),
  }
}

function collectPortalArticleJsonStats(
  artigo: PortalArtigoModel
): PortalArticleJsonTransferStats {
  let images = [artigo.imagemCapaUrl, artigo.imagemThumbUrl, artigo.imagemOgUrl].filter(
    Boolean
  ).length
  if (artigo.configuracaoPillar?.sidebar?.banner?.image?.src) images += 1

  const visitContent = (content: PortalArticleSectionContentItem[] = []) => {
    images += content.filter((item) => item.type === 'image').length
  }
  artigo.conteudo.forEach((block) => {
    if (block.type === 'intro') visitContent(block.section.content)
    if (block.type !== 'topic') return
    block.items.forEach((item) => {
      if (item.type === 'image') images += 1
      if (item.type === 'section') visitContent(item.section.content)
    })
  })

  return {
    rootBlocks: artigo.conteudo.length,
    topics: artigo.conteudo.filter((block) => block.type === 'topic').length,
    images,
  }
}

function createPortalArticleJsonFileName(artigo: PortalArtigoModel) {
  const identity = artigo.slug?.trim() || artigo.id?.toString() || ''
  const safeIdentity = identity
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

  return safeIdentity ? `artigo-${safeIdentity}.json` : 'artigo.json'
}

function sanitizeNullable<T>(
  value: unknown,
  path: string,
  sanitize: (value: unknown, path: string) => T
): T | null | undefined {
  if (value === undefined) return undefined
  if (value === null) return null
  return sanitize(value, path)
}

function optionalRecord(value: unknown, path: string): JsonRecord {
  if (value == null) return {}
  return requireRecord(value, path)
}

function requireRecord(value: unknown, path: string): JsonRecord {
  if (!isRecord(value)) throw transferError('Esperado um objeto.', path)
  return value
}

function optionalArray(value: unknown, path: string): unknown[] {
  if (value == null) return []
  if (!Array.isArray(value)) throw transferError('Esperada uma lista.', path)
  return value
}

function draftString(value: unknown, path: string) {
  if (value == null) return ''
  if (typeof value !== 'string') throw transferError('Esperado um texto.', path)
  return value
}

function optionalString(value: unknown, path: string) {
  if (value == null) return undefined
  if (typeof value !== 'string') throw transferError('Esperado um texto.', path)
  return value
}

function imageSource(value: unknown, path: string) {
  const source = draftString(value, path)
  assertNotEmbeddedImage(source, path)
  return source
}

function optionalImageSource(value: unknown, path: string) {
  const source = optionalString(value, path)
  if (source) assertNotEmbeddedImage(source, path)
  return source
}

function draftNavigationHref(value: unknown, path: string) {
  const href = draftString(value, path)
  assertSafeNavigationHref(href, path)
  return href
}

function optionalNavigationHref(value: unknown, path: string) {
  const href = optionalString(value, path)
  if (href) assertSafeNavigationHref(href, path)
  return href
}

function optionalImageHref(value: unknown, path: string) {
  const href = optionalString(value, path)
  if (!href) return href
  const trimmedHref = href.trim()
  if (!/^https?:\/\//i.test(trimmedHref) && !/^\/(?!\/)/.test(trimmedHref)) {
    throw transferError('Use um link http(s) ou caminho interno iniciado por /.', path)
  }
  return href
}

function assertSafeNavigationHref(value: string, path: string) {
  const href = value.trim()
  if (!href) return
  if (href.startsWith('//')) throw transferError('Link com protocolo não permitido.', path)
  if (href.startsWith('/') || href.startsWith('#')) return
  if (/^(https?:|mailto:|tel:)/i.test(href)) return
  if (!/^[a-z][a-z\d+.-]*:/i.test(href)) return
  throw transferError('Link com protocolo não permitido.', path)
}

function assertNotEmbeddedImage(value: string, path: string) {
  if (/^(data:|blob:)/i.test(value.trim())) {
    throw transferError('Imagens embutidas não são aceitas. Use URL/path ou envie pelo CMS.', path)
  }
}

function draftNumber(value: unknown, path: string) {
  if (value == null) return 0
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw transferError('Esperado um número válido.', path)
  }
  return value
}

function optionalNumber(value: unknown, path: string) {
  if (value == null) return undefined
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw transferError('Esperado um número válido.', path)
  }
  return value
}

function optionalBoolean(value: unknown, path: string) {
  if (value == null) return undefined
  if (typeof value !== 'boolean') throw transferError('Esperado verdadeiro ou falso.', path)
  return value
}

function stringArray(value: unknown, path: string): string[] {
  return optionalArray(value, path).map((item, index) => {
    if (typeof item !== 'string') throw transferError('Esperado um texto.', `${path}.${index}`)
    return item
  })
}

function numberArray(value: unknown, path: string): number[] {
  return optionalArray(value, path).map((item, index) =>
    draftNumber(item, `${path}.${index}`)
  )
}

function sanitizeStringRecord(value: unknown, path: string): Record<string, string> {
  const record = optionalRecord(value, path)
  return Object.entries(record).reduce<Record<string, string>>((result, [key, item]) => {
    assertSafeKey(key, `${path}.${key}`)
    if (typeof item !== 'string') throw transferError('Esperado um texto.', `${path}.${key}`)
    result[key] = item
    return result
  }, {})
}

function optionalEnum<T extends string>(
  value: unknown,
  path: string,
  allowed: readonly T[]
): T | undefined {
  if (value == null) return undefined
  if (typeof value !== 'string' || !allowed.includes(value as T)) {
    throw transferError(`Valor inválido. Use: ${allowed.join(', ')}.`, path)
  }
  return value as T
}

function enumWithDefault<T extends string>(
  value: unknown,
  path: string,
  allowed: readonly T[],
  fallback: T
) {
  return optionalEnum(value, path, allowed) || fallback
}

function requireDiscriminator<T extends string>(
  value: unknown,
  path: string,
  allowed: readonly T[]
): T {
  if (typeof value !== 'string' || !allowed.includes(value as T)) {
    throw transferError(`Tipo desconhecido. Use: ${allowed.join(', ')}.`, path)
  }
  return value as T
}

function assertSafeJsonTree(
  value: unknown,
  path: string,
  depth: number,
  state: { entries: number }
) {
  if (depth > MAX_JSON_DEPTH) throw transferError('JSON possui profundidade excessiva.', path)
  state.entries += 1
  if (state.entries > MAX_JSON_ENTRIES) {
    throw transferError('JSON possui itens demais para importação.', path)
  }
  if (value == null || ['string', 'boolean'].includes(typeof value)) return
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw transferError('Número inválido.', path)
    return
  }
  if (Array.isArray(value)) {
    value.forEach((item, index) => assertSafeJsonTree(item, `${path}.${index}`, depth + 1, state))
    return
  }
  if (!isRecord(value)) throw transferError('Valor JSON inválido.', path)
  Object.entries(value).forEach(([key, item]) => {
    assertSafeKey(key, `${path}.${key}`)
    assertSafeJsonTree(item, `${path}.${key}`, depth + 1, state)
  })
}

function cloneSafeJson(value: unknown, path: string): unknown {
  if (value == null || ['string', 'boolean', 'number'].includes(typeof value)) return value
  if (Array.isArray(value)) {
    return value.map((item, index) => cloneSafeJson(item, `${path}.${index}`))
  }
  const record = requireRecord(value, path)
  return Object.entries(record).reduce<JsonRecord>((result, [key, item]) => {
    assertSafeKey(key, `${path}.${key}`)
    result[key] = cloneSafeJson(item, `${path}.${key}`)
    return result
  }, {})
}

function assertSafeKey(key: string, path: string) {
  if (FORBIDDEN_JSON_KEYS.has(key)) throw transferError('Chave JSON não permitida.', path)
}

function isRecord(value: unknown): value is JsonRecord {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function transferError(message: string, path?: string) {
  return new PortalArticleJsonTransferError(message, path)
}
